// Product units (Umer, 2026-10-05): what ONE package of a product is — 28 g, ½ lb, 1200 mg, 500 ml, a pack of 5.
// Stock everywhere stays a count of packages (store, driver and warehouse stock, orders, transfers); the unit
// only describes the package, so no stock flow changes. Stored on `products` as unit_amount + unit_kind
// (migration 0112): weight in grams (the packager house's unit, shown as oz / lb by the shop's rule — 1 oz = 28 g,
// 1 lb = 448 g, pages/units.ts), mg, ml, or 'each' (1 = a single item, N = a pack of N).
import type { Product, ProductUnitKind } from '../types';
import { GRAMS_PER_LB, GRAMS_PER_OZ, PACK_SIZES } from '../pages/units';

export type ProductUnit = { amount: number; kind: ProductUnitKind };

/** What the unit picker offers: weight entered as g / oz / lb is saved in grams. */
export type UnitInputKind = 'g' | 'oz' | 'lb' | 'mg' | 'ml' | 'each';
export const UNIT_INPUT_KINDS: { kind: UnitInputKind; label: string }[] = [
  { kind: 'g', label: 'grams (g)' },
  { kind: 'oz', label: 'ounces (oz)' },
  { kind: 'lb', label: 'pounds (lb)' },
  { kind: 'mg', label: 'milligrams (mg)' },
  { kind: 'ml', label: 'millilitres (ml)' },
  { kind: 'each', label: 'items (each / pack of)' },
];

const trimNum = (n: number): string => String(Math.round(n * 1000) / 1000);

/** The unit a product carries, or null when it has none yet (the Storage House tags it "No unit"). */
export const productUnit = (p: Pick<Product, 'unitAmount' | 'unitKind'>): ProductUnit | null =>
  p.unitKind && typeof p.unitAmount === 'number' && p.unitAmount > 0 ? { amount: p.unitAmount, kind: p.unitKind } : null;

/** "28 g (1 oz)", "224 g (½ lb)", "1200 mg", "500 ml", "Each", "Pack of 5". */
export function formatProductUnit(u: ProductUnit | null | undefined): string {
  if (!u) return '';
  switch (u.kind) {
    case 'g': {
      const preset = PACK_SIZES.find((p) => p.grams === u.amount);
      return preset ? preset.label : `${trimNum(u.amount)} g`;
    }
    case 'mg': return `${trimNum(u.amount)} mg`;
    case 'ml': return `${trimNum(u.amount)} ml`;
    case 'each': return u.amount === 1 ? 'Each' : `Pack of ${trimNum(u.amount)}`;
  }
}

/** The picker's entry → the stored unit (oz / lb become grams). Null when the amount is not a positive number. */
export function unitFromInput(amount: string, kind: UnitInputKind): ProductUnit | null {
  const n = parseAmount(amount);
  if (n === null || n <= 0) return null;
  if (kind === 'oz') return { amount: n * GRAMS_PER_OZ, kind: 'g' };
  if (kind === 'lb') return { amount: n * GRAMS_PER_LB, kind: 'g' };
  if (kind === 'each') return Number.isInteger(n) ? { amount: n, kind: 'each' } : null;
  return { amount: n, kind };
}

/** A stored unit → the picker's amount + kind (edits show weight in grams). */
export const unitToInput = (u: ProductUnit | null): { amount: string; kind: UnitInputKind } =>
  u ? { amount: trimNum(u.amount), kind: u.kind } : { amount: '', kind: 'g' };

const FRACTIONS: Record<string, number> = { '½': 0.5, '¼': 0.25, '⅛': 0.125, '¾': 0.75, '1/2': 0.5, '1/4': 0.25, '1/8': 0.125, '3/4': 0.75 };
function parseAmount(s: string): number | null {
  const t = s.trim();
  if (t in FRACTIONS) return FRACTIONS[t];
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return parseFloat(t);
}

const NUM = String.raw`(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?|1/2|1/4|1/8|3/4|½|¼|⅛|¾)`;
const DEC = String.raw`(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?)`; // "1,500" is one number (0114)
const PRE = String.raw`(?:^|[^0-9.a-z/]|[a-z]\.)`;
type NameKind = UnitInputKind | 'kg' | 'k';
// The same patterns the migrations use to fill units from names (0112, 0114) — kept in step by the tests.
const NAME_PATTERNS: { re: RegExp; kind: NameKind }[] = [
  { re: new RegExp(String.raw`${PRE}${NUM}\s*(?:g|gr|gram|grams)(?![a-z])`, 'gi'), kind: 'g' },
  { re: new RegExp(String.raw`${PRE}${NUM}\s*(?:oz|ounce|ounces)(?![a-z])`, 'gi'), kind: 'oz' },
  { re: new RegExp(String.raw`${PRE}${NUM}\s*(?:lb|lbs|pound|pounds)(?![a-z])`, 'gi'), kind: 'lb' },
  { re: new RegExp(String.raw`${PRE}${NUM}\s*(?:kg|kgs|kilo|kilos|kilogram|kilograms)(?![a-z])`, 'gi'), kind: 'kg' },
  { re: new RegExp(String.raw`${PRE}${DEC}\s*mg(?![a-z])`, 'gi'), kind: 'mg' },
  { re: new RegExp(String.raw`${PRE}${DEC}\s*ml(?![a-z])`, 'gi'), kind: 'ml' },
  // "5K" on an edible = 5,000 mg (the client, 2026-10-05). On a vape it counts puffs, so it is not read there.
  { re: new RegExp(String.raw`${PRE}${DEC}\s*k(?![a-z])`, 'gi'), kind: 'k' },
];
const PUFFS = /vape|puff|nicotine|pod/;
const scaled = (u: ProductUnit | null, by: number): ProductUnit | null => (u ? { ...u, amount: u.amount * by } : null);
// A box: "Gasgang 2g / 25 each 📦" is a box of 25 vapes — the box is the unit (the client, 2026-10-05).
const BOX = [/\/\s*(\d+)\s*each(?![a-z])/, /box(?:es)?\s+(?:of\s+)?(\d+)(?![0-9])/];

/** A size written in a product's name ("Tuna Breath - 28G", "Gummies / 1200MG", "Buddha Boys 5K") — only when
 *  the name holds exactly one clear size, or names a box ("/ 25 each" → pack of 25); "HP", "L.B" or two sizes
 *  give null (left for a person). */
export function unitFromName(name: string): ProductUnit | null {
  const n = name.toLowerCase();
  for (const re of BOX) {
    const box = re.exec(n);
    if (box && parseInt(box[1], 10) > 0) return { amount: parseInt(box[1], 10), kind: 'each' };
  }
  const found: ProductUnit[] = [];
  for (const { re, kind } of NAME_PATTERNS) {
    if (kind === 'k' && PUFFS.test(n)) continue;
    for (const m of n.matchAll(re)) {
      const raw = m[1].replace(/,/g, '');
      const u = kind === 'kg' ? scaled(unitFromInput(raw, 'g'), 1000)
        : kind === 'k' ? scaled(unitFromInput(raw, 'mg'), 1000)
        : unitFromInput(raw, kind);
      if (u) found.push(u);
    }
  }
  return found.length === 1 ? found[0] : null;
}

/** A CSV "Unit" cell: "28g", "1/2 lb", "1 kg", "1,200 mg", "500ml", "each", "pack of 5", "5 pack", "box of 25". */
export function parseUnitText(text: string): ProductUnit | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (t === 'each' || t === 'single' || t === '1 each') return { amount: 1, kind: 'each' };
  const pack = /^(?:(?:pack|box) of\s*(\d+)|(\d+)\s*(?:pack|pk|pcs|pieces|each|box))$/.exec(t);
  if (pack) return unitFromInput(pack[1] ?? pack[2], 'each');
  const m = new RegExp(String.raw`^${NUM}\s*(g|gr|gram|grams|oz|ounce|ounces|lb|lbs|pound|pounds|kg|kgs|kilo|kilos|mg|ml)$`).exec(t);
  if (!m) return null;
  const amount = m[1].replace(/,/g, '');
  const word = m[2];
  if (/^(kg|kgs|kilo|kilos)$/.test(word)) return scaled(unitFromInput(amount, 'g'), 1000);
  const kind: UnitInputKind = /^(g|gr|gram|grams)$/.test(word) ? 'g' : /^(oz|ounce|ounces)$/.test(word) ? 'oz'
    : /^(lb|lbs|pound|pounds)$/.test(word) ? 'lb' : (word as UnitInputKind);
  return unitFromInput(amount, kind);
}

/** A product made while packing takes the size it was packed in: one size across its pack rows → that
 *  size (grams, or a count for a count-based lot); different sizes or none → no unit (tagged, set by hand). */
export function withPackedUnit<P extends Product>(p: P, outputs: { productId: string; unitGrams: number }[], countUnit?: string | null): P {
  const sizes = [...new Set(outputs.filter((o) => o.productId === p.id && o.unitGrams > 0).map((o) => o.unitGrams))];
  if (sizes.length !== 1) return p;
  return { ...p, unitAmount: sizes[0], unitKind: countUnit ? 'each' : 'g' };
}
